import { useState, useEffect, useRef } from 'react';

/**
 * Commissioner Mode pick countdown. Ticks down locally from the server-provided
 * `timeRemainingSeconds` (re-synced whenever a fresh value arrives, e.g. from a poll) so the
 * display stays smooth between polls instead of jumping only once per poll interval. Renders
 * nothing when no timer is configured (`timeRemainingSeconds` is null).
 *
 * Not yet wired into the live draft board -- that lands in the Phase 6 polling work, once
 * DraftContext gains commissioner-mode-aware polling to actually feed this component fresh
 * values; this component is self-contained and tested ahead of that integration.
 */
function PickTimer({ timeRemainingSeconds, isCommissioner, onSkip, onExtend }) {
  const [remaining, setRemaining] = useState(timeRemainingSeconds);
  const [busy, setBusy] = useState(false);
  const lastSyncedValue = useRef(timeRemainingSeconds);

  useEffect(() => {
    if (timeRemainingSeconds !== lastSyncedValue.current) {
      lastSyncedValue.current = timeRemainingSeconds;
      setRemaining(timeRemainingSeconds);
    }
  }, [timeRemainingSeconds]);

  useEffect(() => {
    if (remaining === null || remaining === undefined || remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining(r => (r === null ? r : Math.max(0, r - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [remaining]);

  if (remaining === null || remaining === undefined) return null;

  const expired = remaining <= 0;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  async function handleSkip() {
    setBusy(true);
    try {
      await onSkip?.();
    } finally {
      setBusy(false);
    }
  }

  async function handleExtend() {
    setBusy(true);
    try {
      await onExtend?.(30);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`pick-timer ${expired ? 'pick-timer--expired' : ''}`}>
      <span className='pick-timer__clock' aria-live='polite'>
        {expired ? "Time's up!" : `${minutes}:${String(seconds).padStart(2, '0')}`}
      </span>
      {expired && isCommissioner && (
        <span className='pick-timer__controls'>
          <button type='button' className='btn btn-secondary' onClick={handleSkip} disabled={busy}>
            Skip Pick
          </button>
          <button type='button' className='btn btn-secondary' onClick={handleExtend} disabled={busy}>
            +30s
          </button>
        </span>
      )}
    </div>
  );
}

export default PickTimer;
